<template>
  <div class="board-detail-view rabiitrip-screen" v-if="board && !accessError">
    <AppHeader />

    <div class="view-content animate-fade-in rabiitrip-shell">
      <div class="container board-unified-container">
        <div
          v-if="boardLoadWarning"
          class="board-load-warning"
          role="status"
        >
          {{ boardLoadWarning }}
        </div>
        <!-- 旅行全体フロー図 (STEP 1: 旅先検討) -->
        <TripWorkflowDiagram :current-step="1" />

        <div class="board-header trip-dark-header">
          <div class="container">
            <div class="shiori-header-top">
              <router-link to="/destination-boards" class="back-link">← 検討一覧に戻る</router-link>
              <div class="header-actions">
                <button type="button" class="btn-header-action" @click="openInviteModal">
                  {{ isOwner ? "メンバー管理" : "メンバー・退出" }}
                </button>
                <button
                  v-if="isOwner"
                  type="button"
                  class="btn-header-action"
                  style="color: #fca5a5; border-color: rgba(252,165,165,0.3);"
                  @click="handleDeleteBoard"
                >
                  削除
                </button>
              </div>
            </div>
            
            <div class="shiori-title-row">
              <h1
                v-if="!isEditingTitle"
                class="trip-title board-title-text"
                :class="{ editable: isOwner }"
                :role="isOwner ? 'button' : undefined"
                :tabindex="isOwner ? 0 : -1"
                @click="startEditTitle"
                @keyup.enter="startEditTitle"
              >
                {{ board.BOARD_TITLE }}
              </h1>
              <input
                v-else
                v-model="tempBoardTitle"
                ref="titleInput"
                @keyup.enter="handleUpdateTitle"
                @blur="handleUpdateTitle"
                class="board-title-input"
                placeholder="ボード名を入力..."
              />
            </div>

            <div class="trip-header-meta">
              <div class="member-avatars">
                <div
                  v-for="member in (board.members || []).slice(0, 3)"
                  :key="member.MEMBER_CODE"
                  class="member-avatar"
                  :title="member.MEMBER_NAME"
                >
                  <span>{{ memberInitial(member.MEMBER_NAME) }}</span>
                </div>
                <div v-if="(board.members || []).length > 3" class="member-avatar">
                  +{{ (board.members || []).length - 3 }}
                </div>
              </div>
              <div class="trip-stats-row">
                <span class="trip-stat">
                  <IconSvg name="user-solid-full" size="sm" />
                  <span class="stat-value">{{ ownerName }}</span>
                </span>
                <span class="trip-stat">
                  <IconSvg name="calendar-solid-full" size="sm" />
                  <span class="stat-value">{{ formatDate(board.ENTRY_YMD) }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="tabs-container clean-tabs" role="tablist">
          <div class="container">
            <button class="clean-tab" :class="{ active: activeTab === 'candidates' }" @click="activeTab = 'candidates'" role="tab" :aria-selected="activeTab === 'candidates' ? 'true' : 'false'" aria-controls="tabpanel-candidates">
              <IconSvg name="list-solid-full" size="sm" class="tab-icon" /> 候補地
            </button>
            <button class="clean-tab" :class="{ active: activeTab === 'matrix' }" @click="activeTab = 'matrix'" role="tab" :aria-selected="activeTab === 'matrix' ? 'true' : 'false'" aria-controls="tabpanel-matrix">
              <IconSvg name="list-solid-full" size="sm" class="tab-icon" /> 比較ボード
            </button>
            <button class="clean-tab" :class="{ active: activeTab === 'chat' }" @click="activeTab = 'chat'" role="tab" :aria-selected="activeTab === 'chat' ? 'true' : 'false'" aria-controls="tabpanel-chat">
              <IconSvg name="envelope-solid-full" size="sm" class="tab-icon" /> 相談チャット
            </button>
          </div>
        </div>

        <div v-show="activeTab === 'candidates'" class="tab-content" role="tabpanel" id="tabpanel-candidates">
          <div class="candidates-action-bar">
            <h2>行き先を出し合って、全員で選ぼう</h2>
            <button type="button" class="btn-add-candidate" id="btn-add-candidate-open" data-track="board-add-candidate-open" @click="showAddCandidateModal = true">
              <IconSvg name="plus-solid-full" size="sm" class="btn-icon" /> 候補地を追加
            </button>
          </div>

          <div v-if="!board.candidates || board.candidates.length === 0" class="empty-state card">
            <p>候補地を出して、みんなの評価を集めよう。</p>
          </div>

          <div v-else class="candidates-grid">
            <CandidateCard
              v-for="(cand, index) in board.candidates"
              :key="cand.CANDIDATE_NO"
              :candidate="cand"
              :my-score="getMyScore(cand)"
              :average-score="getAverageScore(cand)"
              :candidate-image-url="getCandidateImageUrl(cand)"
              @click="openCandidateDetail(cand)"
              class="staggered-reveal"
              :style="{ animationDelay: `${index * 0.05}s` }"
            />
          </div>
        </div>

        <div v-show="activeTab === 'matrix'" class="tab-content" role="tabpanel" id="tabpanel-matrix">
          <ComparisonTable :candidates="board.candidates" :members="board.members" :my-member-code="myMemberCode" :board="board" :is-finalizing="isFinalizing" :can-finalize="canFinalize" @finalize="handleFinalizeCandidate" />
        </div>

        <div v-show="activeTab === 'chat'" class="tab-content chat-tab-content" role="tabpanel" id="tabpanel-chat">
          <p v-if="chatWarning" class="error-message" role="status">{{ chatWarning }}</p>
          <BoardChat :chats="chats" :members="board.members" :my-user-code="myMemberCode || authStore.user?.APP_USER_CODE || ''" :is-loading="boardStore.isLoading" @send-message="handleSendChatMessage" />
        </div>
      </div>
    </div>

    <MemberManageModal :show="showInviteModal" :members="board.members || []" :is-owner="isOwner" :my-member-code="myMemberCode" :owner-user-code="board.ENTRY_USER" :invite-metadata="boardStore.inviteMetadata" :issued-invite-url="issuedInviteUrl" :is-loading="boardStore.isLoading" @close="closeInviteModal" @invite="handleInviteMember" @remove-member="handleRemoveMember" @leave-board="handleLeaveBoard" @update-member="handleUpdateMember" @generate-share-token="handleGenerateShareToken" @rotate-share-token="handleRotateShareToken" @revoke-share-token="handleRevokeShareToken" />
    <CandidateModal :show="showAddCandidateModal" mode="add" :is-loading="boardStore.isLoading" @close="showAddCandidateModal = false" @submit="handleAddCandidate" />
    <CandidateModal :show="!!selectedCandidate" mode="detail" :candidate="selectedCandidate" :my-member-code="myMemberCode" :members="board.members || []" :candidate-image-url="selectedCandidate ? getCandidateImageUrl(selectedCandidate) : ''" :is-owner="isOwner" :is-ai-researching="isAiResearching" :is-loading="boardStore.isLoading" :is-finalizing="isFinalizing" :can-finalize="canFinalize" @close="closeCandidateDetail" @vote-score="submitScore" @clear-score="clearScore" @run-ai="runAiResearch" @finalize="handleFinalizeCandidate" @delete-candidate="handleDeleteCandidate" @update-candidate="handleUpdateCandidate" @consult-ai="handleConsultAiTrip" />
  </div>
  <div v-else-if="accessError || boardLoadWarning" class="board-detail-view rabiitrip-screen">
    <AppHeader />
    <div class="view-content">
      <div class="container">
        <div class="empty-state card" role="alert">
          <p>{{ accessError || boardLoadWarning }}</p>
          <router-link v-if="accessError && accessErrorStatus === 401" to="/login?reason=destination-board" class="btn-add-candidate">ログインする</router-link>
          <router-link v-else-if="accessError" to="/mypage" class="btn-add-candidate">マイページへ戻る</router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import AppHeader from "../components/AppHeader.vue";
import IconSvg from "../components/IconSvg.vue";
import CandidateCard from "../components/CandidateCard.vue";
import ComparisonTable from "../components/ComparisonTable.vue";
import BoardChat from "../components/BoardChat.vue";
import CandidateModal from "../components/CandidateModal.vue";
import MemberManageModal from "../components/MemberManageModal.vue";
import TripWorkflowDiagram from "@/components/TripWorkflowDiagram.vue";
import { useDestinationBoardStore } from "@/store/destination-board-store.js";
import { useAuthStore } from "@/store/authStore.js";
import { useShioriStore } from "@/store/shiori-store.js";
import { getDestinationBoardGuestSession } from "@/utils/guest-session.js";
import {
  beginShioriDestinationImageWorkflow,
  ensureShioriDestinationImage,
  isShioriDestinationImageWorkflowCurrent,
} from "@/utils/ensure-shiori-destination-image.js";

export default {
  name: "DestinationBoardView",
  components: {
    AppHeader,
    IconSvg,
    CandidateCard,
    ComparisonTable,
    BoardChat,
    CandidateModal,
    MemberManageModal,
    TripWorkflowDiagram,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const boardStore = useDestinationBoardStore();
    const authStore = useAuthStore();
    const shioriStore = useShioriStore();

    const activeTab = ref("candidates");
    const showInviteModal = ref(false);
    const issuedInviteUrl = ref("");
    const showAddCandidateModal = ref(false);
    const selectedCandidateId = ref(null);
    const isAiResearching = ref(false);
    const isFinalizing = ref(false);
    const tempScore = ref(0);
    const accessError = ref("");
    const accessErrorStatus = ref(null);
    const boardLoadWarning = ref("");
    const chatWarning = ref("");
    const isEditingTitle = ref(false);
    const tempBoardTitle = ref("");
    const titleInput = ref(null);
    const candidateImageUrls = ref({});
    const candidateImageRequests = new Map();
    const candidateImageFailures = new Set();
    const candidateImageGenerations = new Map();
    let candidateImageBoardId = null;
    let isComponentActive = true;
    let finalizeConfirmationOpen = false;

    const board = computed(() => boardStore.currentBoard);
    const chats = computed(() => (boardStore.chats ? [...boardStore.chats] : []));
    const guestSession = computed(() => getDestinationBoardGuestSession(route.params.id));
    const selectedCandidate = computed(() => {
      if (!selectedCandidateId.value || !board.value?.candidates) return null;
      return board.value.candidates.find((c) => c.CANDIDATE_NO === selectedCandidateId.value) || null;
    });

    const isOwner = computed(() => (
      board.value && authStore.user && board.value.ENTRY_USER === authStore.user.APP_USER_CODE
    ));
    const canFinalize = computed(() => Boolean(isOwner.value));

    const ownerName = computed(() => {
      if (!board.value?.ENTRY_USER) return "";
      const ownerMember = board.value.members?.find((m) => m.APP_USER_CODE === board.value.ENTRY_USER);
      return ownerMember ? ownerMember.MEMBER_NAME : board.value.ENTRY_USER;
    });

    const myMemberCode = computed(() => {
      if (!board.value) return null;
      if (guestSession.value?.memberCode) return guestSession.value.memberCode;
      if (guestSession.value) {
        const guestMember = board.value.members?.find((member) => (
          member.GUEST_ID === guestSession.value.guestId ||
          member.guestId === guestSession.value.guestId ||
          member.guest_id === guestSession.value.guestId ||
          member.MEMBER_NAME === guestSession.value.guestName
        ));
        return guestMember ? guestMember.MEMBER_CODE : null;
      }
      if (!authStore.user) return null;
      const member = board.value.members.find((m) => m.APP_USER_CODE === authStore.user.APP_USER_CODE);
      return member ? member.MEMBER_CODE : null;
    });

    let pollingInterval = null;

    const startPolling = (boardId) => {
      if (pollingInterval) return;
      pollingInterval = setInterval(() => {
        void pollBoardData(boardId);
      }, 5000);
    };

    const stopPolling = () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
        pollingInterval = null;
      }
    };

    const handleVisibilityChange = () => {
      const boardId = route.params.id;
      if (document.hidden) {
        stopPolling();
      } else if (!accessError.value) {
        startPolling(boardId);
      }
    };

    const trackEvent = (eventName, params = {}) => {
      try {
        if (window.gtag) {
          window.gtag("event", eventName, params);
        } else {
          console.log(`[Tracking Event] ${eventName}:`, params);
        }
      } catch (err) {
        console.error("Tracking error (safe):", err);
      }
    };

    let originalRobotsExist = false;
    let originalRobotsVal = "";

    onMounted(async () => {
      const metaRobots = document.querySelector('meta[name="robots"]');
      if (metaRobots) {
        originalRobotsExist = true;
        originalRobotsVal = metaRobots.getAttribute("content") || "";
      }

      let targetMeta = metaRobots;
      if (!targetMeta) {
        targetMeta = document.createElement("meta");
        targetMeta.setAttribute("name", "robots");
        document.head.appendChild(targetMeta);
      }
      targetMeta.setAttribute("content", "noindex, nofollow");

      const boardId = route.params.id;
      await loadBoardData(boardId);

      if (!document.hidden && !accessError.value) {
        startPolling(boardId);
      }
      document.addEventListener("visibilitychange", handleVisibilityChange);
    });

    onUnmounted(() => {
      isComponentActive = false;
      candidateImageGenerations.forEach((generation, candidateNo) => {
        candidateImageGenerations.set(candidateNo, generation + 1);
      });
      candidateImageRequests.clear();
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      Object.values(candidateImageUrls.value).forEach((url) => URL.revokeObjectURL(url));

      const metaRobots = document.querySelector('meta[name="robots"]');
      if (metaRobots) {
        if (originalRobotsExist) {
          metaRobots.setAttribute("content", originalRobotsVal || "index, follow");
        } else {
          metaRobots.remove();
        }
      }

      document.title = "RabiiTrip - みんなで旅行のしおりを作れるサービス";
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute("content", "RabiiTrip travel planning service.");
      }
      const metaOgTitle = document.querySelector('meta[property="og:title"]');
      if (metaOgTitle) {
        metaOgTitle.setAttribute("content", "RabiiTrip");
      }
      const metaOgDesc = document.querySelector('meta[property="og:description"]');
      if (metaOgDesc) {
        metaOgDesc.setAttribute("content", "RabiiTrip travel planning service.");
      }
    });

    const loadBoardData = async (boardId) => {
      if (!localStorage.getItem("token") && !getDestinationBoardGuestSession(boardId)) {
        accessErrorStatus.value = 401;
        accessError.value = "このボードを開くには、共有リンクから参加するかログインしてください。";
        return;
      }
      try {
        accessError.value = "";
        accessErrorStatus.value = null;
        boardLoadWarning.value = "";
        await boardStore.fetchBoardDetail(boardId);
      } catch (err) {
        console.error("loadBoardData error:", err);
        const status = err.response?.status;
        if ([401, 403, 404].includes(status)) {
          accessErrorStatus.value = status;
          accessError.value = status === 401
            ? "ログインの有効期限が切れました。もう一度ログインしてください。"
            : status === 403
              ? "このボードを表示する権限がありません。"
              : "このボードは見つかりませんでした。";
        } else {
          boardLoadWarning.value = "ボード情報を読み込めませんでした。時間をおいて再度お試しください。";
        }
        return;
      }

      try {
        await boardStore.fetchChatHistory(boardId);
        chatWarning.value = "";
      } catch (err) {
        console.error("loadChatHistory error:", err);
        chatWarning.value = "チャットを更新できませんでした。直前の内容を表示しています。";
      }
    };

    const pollBoardData = async (boardId) => {
      try {
        await boardStore.fetchBoardDetail(boardId);
        boardLoadWarning.value = "";
      } catch (err) {
        console.error("pollBoardDetail error:", err);
        const status = err.response?.status;
        if ([401, 403, 404].includes(status)) {
          accessErrorStatus.value = status;
          accessError.value = status === 401
            ? "ログインの有効期限が切れました。もう一度ログインしてください。"
            : status === 403
              ? "このボードを表示する権限がありません。"
              : "このボードは見つかりませんでした。";
          stopPolling();
          return;
        }
        boardLoadWarning.value = "ボード情報を更新できませんでした。直前の内容を表示しています。";
      }

      try {
        await boardStore.fetchChatHistory(boardId);
        chatWarning.value = "";
      } catch (err) {
        console.error("pollChatHistory error:", err);
        chatWarning.value = "チャットを更新できませんでした。直前の内容を表示しています。";
      }
    };

    watch(board, (newBoard) => {
      if (!newBoard) return;
      const title = `${newBoard.BOARD_TITLE} - 旅行先検討 | RabiiTrip`;
      const description = `${newBoard.BOARD_TITLE} のメンバーと旅行先を検討するためのプライベートボードです。`;
      document.title = title;

      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement("meta");
        metaDesc.setAttribute("name", "description");
        document.head.appendChild(metaDesc);
      }
      metaDesc.setAttribute("content", description);

      let metaOgTitle = document.querySelector('meta[property="og:title"]');
      if (!metaOgTitle) {
        metaOgTitle = document.createElement("meta");
        metaOgTitle.setAttribute("property", "og:title");
        document.head.appendChild(metaOgTitle);
      }
      metaOgTitle.setAttribute("content", title);

      let metaOgDesc = document.querySelector('meta[property="og:description"]');
      if (!metaOgDesc) {
        metaOgDesc = document.createElement("meta");
        metaOgDesc.setAttribute("property", "og:description");
        document.head.appendChild(metaOgDesc);
      }
      metaOgDesc.setAttribute("content", description);
    }, { immediate: true });

    const invalidateCandidateImageRequest = (candidateNo) => {
      const nextGeneration = (candidateImageGenerations.get(candidateNo) || 0) + 1;
      candidateImageGenerations.set(candidateNo, nextGeneration);
      candidateImageRequests.delete(candidateNo);
    };

    const fetchCandidateImage = async (candidate) => {
      const boardId = board.value?.BOARD_ID;
      const rawCandidateNo = candidate?.CANDIDATE_NO;
      if (!boardId || rawCandidateNo === null || rawCandidateNo === undefined || rawCandidateNo === "") return;
      const candidateNo = String(rawCandidateNo);
      if (candidateImageUrls.value[candidateNo] ||
          candidateImageRequests.has(candidateNo) || candidateImageFailures.has(candidateNo)) return;
      const generation = (candidateImageGenerations.get(candidateNo) || 0) + 1;
      candidateImageGenerations.set(candidateNo, generation);
      candidateImageRequests.set(candidateNo, generation);
      try {
        const blob = await boardStore.fetchCandidateImage(boardId, candidateNo);
        const requestIsCurrent = () => (
          isComponentActive &&
          candidateImageGenerations.get(candidateNo) === generation &&
          candidateImageRequests.get(candidateNo) === generation &&
          String(board.value?.BOARD_ID || "") === String(boardId) &&
          board.value?.candidates?.some((item) => String(item.CANDIDATE_NO) === candidateNo)
        );
        if (!requestIsCurrent()) return;
        if (!(blob instanceof Blob) || !blob.size || !blob.type.startsWith("image/")) {
          candidateImageFailures.add(candidateNo);
          return;
        }
        const objectUrl = URL.createObjectURL(blob);
        if (!requestIsCurrent()) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        candidateImageUrls.value = {
          ...candidateImageUrls.value,
          [candidateNo]: objectUrl,
        };
      } catch (_error) {
        if (candidateImageGenerations.get(candidateNo) === generation) {
          candidateImageFailures.add(candidateNo);
        }
      } finally {
        if (candidateImageRequests.get(candidateNo) === generation) {
          candidateImageRequests.delete(candidateNo);
        }
      }
    };

    const refreshCandidateImage = (candidate) => {
      const rawCandidateNo = candidate?.CANDIDATE_NO;
      if (rawCandidateNo === null || rawCandidateNo === undefined || rawCandidateNo === "") return;
      const candidateNo = String(rawCandidateNo);
      invalidateCandidateImageRequest(candidateNo);
      const oldUrl = candidateImageUrls.value[candidateNo];
      if (oldUrl) URL.revokeObjectURL(oldUrl);
      const next = { ...candidateImageUrls.value };
      delete next[candidateNo];
      candidateImageUrls.value = next;
      candidateImageFailures.delete(candidateNo);
      fetchCandidateImage(candidate);
    };

    const getCandidateImageUrl = (candidate) => {
      const candidateNo = candidate?.CANDIDATE_NO;
      return candidateNo === null || candidateNo === undefined
        ? ""
        : candidateImageUrls.value[String(candidateNo)] || "";
    };

    watch(
      () => `${board.value?.BOARD_ID || ""}:${board.value?.candidates?.map((candidate) => candidate.CANDIDATE_NO).join(",") || ""}`,
      () => {
        const candidates = board.value?.candidates || [];
        const currentBoardId = board.value?.BOARD_ID || null;
        if (candidateImageBoardId !== null && String(candidateImageBoardId) !== String(currentBoardId)) {
          candidateImageRequests.forEach((_generation, candidateNo) => {
            invalidateCandidateImageRequest(candidateNo);
          });
          Object.entries(candidateImageUrls.value).forEach(([candidateNo, url]) => {
            invalidateCandidateImageRequest(candidateNo);
            URL.revokeObjectURL(url);
          });
          candidateImageUrls.value = {};
          candidateImageFailures.clear();
        }
        candidateImageBoardId = currentBoardId;
        const activeIds = new Set(candidates.map((candidate) => String(candidate.CANDIDATE_NO)));
        Object.entries(candidateImageUrls.value).forEach(([candidateNo, url]) => {
          if (!activeIds.has(candidateNo)) {
            invalidateCandidateImageRequest(candidateNo);
            URL.revokeObjectURL(url);
            const next = { ...candidateImageUrls.value };
            delete next[candidateNo];
            candidateImageUrls.value = next;
          }
        });
        candidateImageRequests.forEach((_generation, candidateNo) => {
          if (!activeIds.has(candidateNo)) invalidateCandidateImageRequest(candidateNo);
        });
        candidateImageFailures.forEach((candidateNo) => {
          if (!activeIds.has(candidateNo)) candidateImageFailures.delete(candidateNo);
        });
        candidates.forEach(fetchCandidateImage);
      },
      { immediate: true },
    );

    const handleSendChatMessage = async (messageText) => {
      if (!board.value) return;
      try {
        await boardStore.createChatMessage(board.value.BOARD_ID, messageText);
      } catch (err) {
        alert("メッセージの送信に失敗しました");
      }
    };

    const scoreCategories = ["sightseeing", "activity", "food", "cost"];

    const normalizeScoreDetails = (value) => {
      if (!value || typeof value !== "object") return null;
      const source = value.scores || value.scoreDetails || value.score_details || value;
      const details = {
        sightseeing: Number(source.sightseeingScore ?? source.sightseeing ?? source.tourism ?? source.SIGHTSEEING ?? 0) || 0,
        activity: Number(source.activityScore ?? source.activity ?? source.ACTIVITY ?? 0) || 0,
        food: Number(source.foodScore ?? source.food ?? source.gourmet ?? source.GOURMET ?? 0) || 0,
        cost: Number(source.costScore ?? source.cost ?? source.COST ?? 0) || 0,
      };
      return scoreCategories.some((key) => details[key] > 0) ? details : null;
    };

    const scoreTotal = (score) => {
      const details = normalizeScoreDetails(score);
      if (details) return scoreCategories.reduce((sum, key) => sum + Number(details[key] || 0), 0);
      if (score && typeof score === "object") return Number(score.SCORE_VAL ?? score.scoreVal ?? score.score ?? 0) || 0;
      return Number(score || 0) || 0;
    };
    const getAverageScore = (cand) => {
      if (!cand) return 0;
      if (cand.scores && cand.scores.length > 0) {
        const totals = cand.scores.map((score) => scoreTotal(score)).filter((score) => score > 0);
        if (totals.length) return totals.reduce((sum, score) => sum + score, 0) / totals.length;
      }
      if (cand.averageScore !== undefined) return Number(cand.averageScore) || 0;
      return 0;
    };

    const getMyScore = (cand) => {
      if (!cand || !myMemberCode.value) return 0;
      if (cand.memberScores && cand.memberScores[myMemberCode.value] !== undefined) return scoreTotal(cand.memberScores[myMemberCode.value]);
      const score = cand.scores?.find((s) => s.MEMBER_CODE === myMemberCode.value || s.memberCode === myMemberCode.value);
      return scoreTotal(score);
    };

    const submitScore = async (payload) => {
      if (!selectedCandidate.value || !board.value) return;
      const val = typeof payload === "object" ? payload.scoreVal ?? payload.score : payload;
      const scoreDetails = typeof payload === "object" ? {
        sightseeingScore: payload.sightseeingScore ?? payload.scores?.sightseeing ?? payload.scoreDetails?.sightseeing ?? 0,
        activityScore: payload.activityScore ?? payload.scores?.activity ?? payload.scoreDetails?.activity ?? 0,
        foodScore: payload.foodScore ?? payload.scores?.food ?? payload.scores?.gourmet ?? payload.scoreDetails?.food ?? payload.scoreDetails?.gourmet ?? 0,
        costScore: payload.costScore ?? payload.scores?.cost ?? payload.scoreDetails?.cost ?? 0,
      } : null;
      const memberCode = typeof payload === "object" ? payload.memberCode : myMemberCode.value;
      if (!memberCode) return;
      try {
        await boardStore.registerScore(board.value.BOARD_ID, selectedCandidate.value.CANDIDATE_NO, val, memberCode, scoreDetails);
        await nextTick();
        if (memberCode === myMemberCode.value) tempScore.value = val;
        trackEvent("vote_candidate", {
          board_id: board.value.BOARD_ID,
          candidate_id: selectedCandidate.value.CANDIDATE_NO,
          member_code: memberCode,
          score: val,
          score_details: scoreDetails,
        });
      } catch (err) {
        alert("スコアの登録に失敗しました");
      }
    };

    const clearScore = async (memberCode = myMemberCode.value) => {
      if (!selectedCandidate.value || !board.value || !memberCode) return;
      try {
        await boardStore.deleteScore(board.value.BOARD_ID, selectedCandidate.value.CANDIDATE_NO, memberCode);
        await nextTick();
        if (memberCode === myMemberCode.value) tempScore.value = 0;
        trackEvent("clear_score", {
          board_id: board.value.BOARD_ID,
          candidate_id: selectedCandidate.value.CANDIDATE_NO,
          member_code: memberCode,
        });
      } catch (err) {
        alert("スコアの削除に失敗しました");
      }
    };

    const openCandidateDetail = (cand) => {
      selectedCandidateId.value = cand.CANDIDATE_NO;
      tempScore.value = getMyScore(cand);
      trackEvent("open_candidate_detail", {
        board_id: board.value?.BOARD_ID || route.params.id,
        candidate_id: cand.CANDIDATE_NO,
      });
    };

    const closeCandidateDetail = () => {
      if (selectedCandidate.value && board.value) {
        trackEvent("close_candidate_detail", {
          board_id: board.value.BOARD_ID,
          candidate_id: selectedCandidate.value.CANDIDATE_NO,
        });
      }
      selectedCandidateId.value = null;
    };

    const handleInviteMember = async (payload) => {
      if (!board.value) return;
      try {
        await boardStore.inviteMember(board.value.BOARD_ID, payload);
        closeInviteModal();
        trackEvent("invite_member", {
          board_id: board.value.BOARD_ID,
          invited_count: 1,
        });
      } catch (err) {
        alert("操作に失敗しました。もう一度お試しください。");
      }
    };

    const openInviteModal = async () => {
      issuedInviteUrl.value = "";
      showInviteModal.value = true;
      if (!isOwner.value || !board.value) return;
      try {
        await boardStore.fetchShareTokenMetadata(board.value.BOARD_ID);
      } catch {
        alert("招待リンク情報を取得できませんでした");
      }
    };

    const closeInviteModal = () => {
      issuedInviteUrl.value = "";
      showInviteModal.value = false;
    };

    const handleRemoveMember = async (memberCode) => {
      if (!board.value) return;
      try {
        await boardStore.removeMember(board.value.BOARD_ID, memberCode);
      } catch (err) {
        alert("メンバーの削除に失敗しました");
      }
    };

    const handleLeaveBoard = async (memberCode) => {
      if (!board.value) return;
      try {
        await boardStore.removeMember(board.value.BOARD_ID, memberCode);
        closeInviteModal();
        await router.push("/mypage");
      } catch (err) {
        alert("ボードからの退出に失敗しました");
      }
    };

    const handleAddCandidate = async ({ form, file }) => {
      if (!board.value) return;
      try {
        const formData = new FormData();
        formData.append("candidateName", form.candidateName);
        if (form.memoText) formData.append("memoText", form.memoText);
        if (form.urlText) formData.append("urlText", form.urlText);
        if (form.estimatedBudget) formData.append("estimatedBudget", form.estimatedBudget);
        if (form.estimatedDays) formData.append("estimatedDays", form.estimatedDays);
        if (form.itinerarySample) formData.append("itinerarySample", form.itinerarySample);
        if (form.budgetBreakdown) formData.append("budgetBreakdown", JSON.stringify(form.budgetBreakdown));
        if (file) formData.append("image", file);

        const res = await boardStore.createCandidate(board.value.BOARD_ID, formData);
        showAddCandidateModal.value = false;
        const newlyAdded = board.value.candidates.find((c) => c.CANDIDATE_NO === res.candidateNo);
        trackEvent("add_candidate", {
          board_id: board.value.BOARD_ID,
          candidate_id: newlyAdded ? newlyAdded.CANDIDATE_NO : "unknown",
          has_image: !!file,
        });
        if (file) {
          trackEvent("upload_candidate_image", {
            board_id: board.value.BOARD_ID,
            candidate_id: newlyAdded ? newlyAdded.CANDIDATE_NO : "unknown",
            action: "create",
          });
        }
      } catch {
        alert("操作に失敗しました。もう一度お試しください。");
      }
    };

    const handleDeleteCandidate = async (candidateNo) => {
      if (!board.value || !confirm("本当に実行しますか？")) return;
      try {
        await boardStore.deleteCandidate(board.value.BOARD_ID, candidateNo);
        selectedCandidateId.value = null;
      } catch (err) {
        alert("候補地の削除に失敗しました");
      }
    };

    const runAiResearch = async (candidateNo) => {
      if (!board.value) return;
      isAiResearching.value = true;
      try {
        await boardStore.aiResearchCandidate(board.value.BOARD_ID, candidateNo);
        await nextTick();
        trackEvent("run_ai_research", {
          board_id: board.value.BOARD_ID,
          candidate_id: candidateNo,
        });
        trackEvent("ai_research_budget_autosave", {
          board_id: board.value.BOARD_ID,
          candidate_id: candidateNo,
        });
      } catch (err) {
        alert("操作に失敗しました。もう一度お試しください。");
      } finally {
        isAiResearching.value = false;
      }
    };

    const handleFinalizeCandidate = async (candidateNo) => {
      if (!board.value || !canFinalize.value || isFinalizing.value || finalizeConfirmationOpen) return;
      finalizeConfirmationOpen = true;
      const confirmed = confirm("本当に実行しますか？");
      finalizeConfirmationOpen = false;
      if (!confirmed) return;
      const imageWorkflowSession = beginShioriDestinationImageWorkflow();
      isFinalizing.value = true;
      try {
        const candidate = board.value.candidates?.find((item) => item.CANDIDATE_NO === candidateNo);
        const result = await boardStore.finalizeBoardToShiori(board.value.BOARD_ID, candidateNo);
        if (!isShioriDestinationImageWorkflowCurrent(imageWorkflowSession)) return;
        const shioriId = result?.SHIORI_NOV || result?.shioriId || result?.shiori_id;
        if (shioriId) {
          await ensureShioriDestinationImage({
            shioriId,
            destinationName: candidate?.CANDIDATE_NAME,
            explicitCandidateImage: candidate
              ? ({ signal, timeout }) => boardStore.fetchCandidateImage(board.value.BOARD_ID, candidateNo, { signal, timeout })
              : null,
            shioriStore,
            session: imageWorkflowSession,
          });
          if (!isShioriDestinationImageWorkflowCurrent(imageWorkflowSession)) return;
          trackEvent("convert_to_shiori", {
            board_id: board.value.BOARD_ID,
            candidate_id: candidateNo,
            shiori_id: shioriId,
          });
          await router.push(`/shiori/${shioriId}`);
        } else {
          alert("操作に失敗しました。もう一度お試しください。");
        }
      } catch (err) {
        if (isShioriDestinationImageWorkflowCurrent(imageWorkflowSession)) {
          alert("操作に失敗しました。もう一度お試しください。");
        }
      } finally {
        isFinalizing.value = false;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return "";
      return new Date(dateString).toLocaleDateString("ja-JP");
    };

    const memberInitial = (name = "") => String(name || "?").trim().charAt(0) || "?";

    const startEditTitle = () => {
      if (!board.value || !isOwner.value) return;
      tempBoardTitle.value = board.value.BOARD_TITLE;
      isEditingTitle.value = true;
      nextTick(() => {
        titleInput.value?.focus();
      });
    };

    const handleUpdateTitle = async () => {
      if (!board.value || !tempBoardTitle.value.trim()) return;
      try {
        await boardStore.updateBoard(board.value.BOARD_ID, tempBoardTitle.value.trim());
        isEditingTitle.value = false;
      } catch (err) {
        alert("ボードタイトルの更新に失敗しました");
      }
    };

    const handleDeleteBoard = async () => {
      if (!board.value) return;
      if (confirm(`本当にボード「${board.value.BOARD_TITLE}」を削除しますか？\nこの操作は取り消せません。`)) {
        try {
          await boardStore.deleteBoard(board.value.BOARD_ID);
          await router.push("/mypage");
        } catch (err) {
          alert("操作に失敗しました。もう一度お試しください。");
        }
      }
    };

    const handleUpdateCandidate = async ({ candidateNo, formData }) => {
      if (!board.value) return;
      try {
        await boardStore.updateCandidate(board.value.BOARD_ID, candidateNo, formData);
        await nextTick();
        if (formData.has("image")) {
          trackEvent("upload_candidate_image", {
            board_id: board.value.BOARD_ID,
            candidate_id: candidateNo,
            action: "update",
          });
          refreshCandidateImage({ CANDIDATE_NO: candidateNo });
        }
        const updated = board.value.candidates.find((c) => c.CANDIDATE_NO === candidateNo);
        if (!updated) selectedCandidateId.value = null;
        alert("候補地情報を更新しました");
      } catch (err) {
        alert("操作に失敗しました。もう一度お試しください。");
      }
    };

    const handleConsultAiTrip = async (candidate) => {
      if (!candidate) return;
      const query = {
        destination: candidate.CANDIDATE_NAME,
        days: candidate.ESTIMATED_DAYS,
        travelers: board.value?.members?.length || 1,
        budgetPerPerson: candidate.ESTIMATED_BUDGET,
      };
      Object.keys(query).forEach((key) => {
        if (query[key] === "" || query[key] === null || query[key] === undefined) {
          delete query[key];
        }
      });
      try {
        await router.push({ path: "/ai-trip", query });
      } catch (error) {
        console.error("AI旅行相談画面への遷移エラー:", error);
        alert("AI旅行相談画面を開けませんでした。もう一度お試しください。");
      }
    };
    const handleUpdateMember = async ({ memberCode, memberData }) => {
      if (!board.value) return;
      try {
        await boardStore.updateMember(board.value.BOARD_ID, memberCode, memberData);
        alert("メンバー情報を更新しました");
      } catch (err) {
        alert("操作に失敗しました。もう一度お試しください。");
      }
    };

    const handleGenerateShareToken = async () => {
      if (!board.value) return;
      try {
        const result = await boardStore.generateShareToken(board.value.BOARD_ID);
        const inviteToken = result?.inviteToken || result?.invite_token;
        if (!inviteToken) throw new Error("Invite token was not issued");
        issuedInviteUrl.value = `${window.location.origin}/destination-boards/join/${encodeURIComponent(inviteToken)}`;
      } catch (err) {
        alert("操作に失敗しました。もう一度お試しください。");
      }
    };

    const handleRotateShareToken = async () => {
      if (!board.value) return;
      if (!confirm("現在の招待リンクを無効にして、新しいリンクを発行しますか？")) return;
      try {
        const result = await boardStore.rotateShareToken(board.value.BOARD_ID);
        const inviteToken = result?.inviteToken || result?.invite_token;
        if (!inviteToken) throw new Error("Invite token was not issued");
        issuedInviteUrl.value = `${window.location.origin}/destination-boards/join/${encodeURIComponent(inviteToken)}`;
      } catch {
        alert("招待リンクを再発行できませんでした");
      }
    };

    const handleRevokeShareToken = async (tokenId) => {
      if (!board.value || !tokenId) return;
      if (!confirm("この招待リンクを無効にしますか？")) return;
      try {
        await boardStore.revokeShareToken(board.value.BOARD_ID, tokenId);
        issuedInviteUrl.value = "";
      } catch {
        alert("招待リンクを無効化できませんでした");
      }
    };

    return {
      activeTab,
      showInviteModal,
      issuedInviteUrl,
      showAddCandidateModal,
      selectedCandidate,
      isAiResearching,
      isFinalizing,
      board,
      chats,
      guestSession,
      accessError,
      accessErrorStatus,
      boardLoadWarning,
      chatWarning,
      isOwner,
      canFinalize,
      ownerName,
      authStore,
      boardStore,
      myMemberCode,
      getCandidateImageUrl,
      handleSendChatMessage,
      getAverageScore,
      getMyScore,
      submitScore,
      clearScore,
      openCandidateDetail,
      closeCandidateDetail,
      handleInviteMember,
      openInviteModal,
      closeInviteModal,
      handleRemoveMember,
      handleLeaveBoard,
      handleAddCandidate,
      handleGenerateShareToken,
      handleRotateShareToken,
      handleRevokeShareToken,
      handleDeleteCandidate,
      runAiResearch,
      handleFinalizeCandidate,
      formatDate,
      memberInitial,
      isEditingTitle,
      tempBoardTitle,
      titleInput,
      startEditTitle,
      handleUpdateTitle,
      handleDeleteBoard,
      handleUpdateCandidate,
      handleConsultAiTrip,
      handleUpdateMember,
    };
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;700&family=IBM+Plex+Sans:wght@400;600;700&display=swap');

/* Board-local theme variables inherited by child components. */
.board-detail-view {
  --primary-color: #b4cf9e;
  --primary-hover: #8ca87a;
  --primary-dark: #8ca87a;
  --primary-rgb: 180, 207, 158;
  --accent-gold: #d4af37;
  --bg-primary: #ffffff;
  --bg-secondary: #f8fafc;
  --text-main: #0f172a;
  --text-muted: #64748b;
  --border-color: rgba(226, 232, 240, 0.8);
  --card-shadow: 0 10px 30px rgba(0, 0, 0, 0.02), inset 0 1px 0 rgba(255, 255, 255, 0.6);
  min-height: 100vh;
  background: var(--color-background);
  font-family: 'IBM Plex Sans', sans-serif;
}

.view-content {
  padding: 2rem 1rem;
}

.board-unified-container {
  max-width: 1040px !important;
  margin: 0 auto;
}

.board-load-warning {
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  border: 1px solid #d8b45a;
  border-radius: var(--radius-md);
  background: #fff8df;
  color: #654b0b;
  font-weight: 700;
}

/* フェードインアニメーション */
.animate-fade-in {
  opacity: 0;
  animation: fadeIn 0.5s ease-out forwards;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}

.back-link {
  display: inline-block;
  margin-bottom: 0.5rem;
  color: var(--text-muted);
  text-decoration: none;
  font-weight: 600;
  transition: color 0.2s;
}

.back-link:hover {
  color: var(--primary-color);
}

.board-header {
  margin-bottom: 1rem;
  border-radius: var(--radius-lg);
  overflow: hidden;
}

.board-title {
  font-size: 1.75rem;
  font-weight: 800;
  color: var(--text-main);
  margin: 0;
  font-family: 'Space Grotesk', sans-serif;
}

.board-title-text {
  display: -webkit-box;
  max-width: 100%;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  word-break: break-word;
}

.board-title-text.editable {
  cursor: text;
}

.board-title-input {
  width: min(100%, 520px);
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  font-size: 1.75rem;
  font-weight: 800;
  line-height: 1.25;
  color: #1e293b;
  border: 1.5px solid var(--primary-color);
  border-radius: 8px;
  padding: 0.25rem 0.5rem;
  background: rgba(255, 255, 255, 0.9);
  outline: none;
}



/* カード共通 */
.card {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-radius: 20px;
  box-shadow: var(--card-shadow);
  border: 1px solid var(--color-border);
  margin-bottom: 2rem;
}

/* 候補リスト */
.candidates-action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.candidates-action-bar h2 {
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--text-main);
  font-family: 'Space Grotesk', sans-serif;
}

.btn-add-candidate {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  background: var(--primary-color);
  color: white;
  border: none;
  padding: 0.625rem 1.25rem;
  font-weight: 700;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-add-candidate:hover {
  background: var(--primary-hover);
}

.btn-icon {
  fill: currentColor;
}

.empty-state {
  padding: 3rem;
  text-align: center;
  color: var(--text-muted);
}

.candidates-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}

@media (min-width: 640px) {
  .candidates-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1024px) {
  .candidates-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

/* リビールアニメーション */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.staggered-reveal {
  opacity: 0;
  animation: fadeInUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

@media (max-width: 640px) {
  .board-header {
    position: relative;
    padding: 0.875rem 1rem 2.5rem;
  }

  .header-left {
    min-width: 0;
  }

  .title-container {
    padding-right: 0.25rem;
  }

  .board-title {
    font-size: 1.35rem;
    line-height: 1.25;
  }

  .btn-edit-title {
    display: none;
  }

  .board-title-input {
    width: 100%;
    font-size: 1.35rem;
  }

  .board-meta {
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.1rem;
    max-width: 42%;
    font-size: 0.65rem;
    line-height: 1.2;
    text-align: right;
  }

  .board-meta span {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .header-right {
    flex-direction: row !important;
    align-items: center !important;
    justify-content: flex-end;
    gap: 0.5rem !important;
    padding-right: 0;
  }

  .section-label {
    display: none;
  }

  .btn-delete-board {
    position: static;
    flex: 0 0 auto;
    display: inline-grid;
    place-items: center;
    width: 44px;
    height: 44px;
    min-width: 44px;
    min-height: 44px;
    padding: 0 !important;
    justify-content: center;
    align-items: center;
  }

  .candidates-grid {
    display: flex;
    gap: 1rem;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    padding: 0 1rem 0.5rem;
    margin: 0 -1rem 2rem;
  }

  .candidates-grid > * {
    flex: 0 0 80vw;
    scroll-snap-align: start;
  }
}
</style>
